import {
  ConstitutionExcelPreviewItem,
  ConstitutionExcelRangeMode,
  ConstitutionExcelRow,
  ConstitutionExcelValidationError,
} from '../types/ConstitutionExcel';
import {
  HoldingClassForConstitutionExcel,
  initConstitutionNumerations,
} from './initConstitutionNumerations';
import { getHoldingClassIdForRow } from './validateConstitutionExcelRows';

type AssignedRange = {
  from: number;
  to: number;
  rowIndex: number;
};

const rangesOverlap = (
  rangeA: { from: number; to: number },
  rangeB: { from: number; to: number }
): boolean => !(rangeA.to < rangeB.from || rangeB.to < rangeA.from);

const hasRangeValue = (value: unknown): boolean =>
  value !== undefined && value !== null && String(value).trim() !== '';

export const getRangeMode = (
  rows: ConstitutionExcelRow[]
): ConstitutionExcelRangeMode => {
  const explicitCount = rows.filter((row) => row.hasExplicitRange).length;

  if (!explicitCount) return 'automatic';
  if (explicitCount === rows.length) return 'explicit';
  return 'mixed';
};

type AssignRangesParams = {
  rows: ConstitutionExcelRow[];
  holdingClasses: HoldingClassForConstitutionExcel[];
  rowIndexesWithErrors?: Set<number>;
};

export const assignConstitutionExcelRanges = ({
  rows,
  holdingClasses,
  rowIndexesWithErrors = new Set(),
}: AssignRangesParams): {
  items: ConstitutionExcelPreviewItem[];
  overlapErrors: ConstitutionExcelValidationError[];
} => {
  const numerationState = initConstitutionNumerations(holdingClasses);
  const currentFrom = numerationState ? { ...numerationState.from } : {};
  const numerationRefs = numerationState?.refs || {};
  const assignedByNumerationKey: Record<string, AssignedRange[]> = {};
  const overlapErrors: ConstitutionExcelValidationError[] = [];

  const items = rows.map((row) => {
    const holdingClassId = getHoldingClassIdForRow(row, holdingClasses);
    const holdingClass = holdingClasses.find(
      (holdingClassItem) => holdingClassItem._id === holdingClassId
    );
    const numerationKey = numerationRefs[holdingClassId] || holdingClassId;

    let shareFrom = 0;
    let shareTo = 0;

    if (!rowIndexesWithErrors.has(row.rowIndex)) {
      if (row.hasExplicitRange && row.shareFrom && row.shareTo) {
        shareFrom = row.shareFrom;
        shareTo = row.shareTo;
      } else if (row.shares > 0) {
        shareFrom = currentFrom[numerationKey] || 1;
        shareTo = shareFrom + row.shares - 1;
      }

      if (shareFrom > 0 && shareTo >= shareFrom) {
        const assignedRanges = assignedByNumerationKey[numerationKey] || [];

        assignedRanges.forEach((assignedRange) => {
          if (
            rangesOverlap(assignedRange, { from: shareFrom, to: shareTo })
          ) {
            overlapErrors.push({
              code: 'OVERLAPPING_RANGES',
              rowIndex: row.rowIndex,
              cif: row.cif,
              detail: `${shareFrom}-${shareTo}`,
            });
          }
        });

        assignedByNumerationKey[numerationKey] = [
          ...assignedRanges,
          { from: shareFrom, to: shareTo, rowIndex: row.rowIndex },
        ];

        currentFrom[numerationKey] = Math.max(
          currentFrom[numerationKey] || 1,
          shareTo + 1
        );
      }
    }

    return {
      rowIndex: row.rowIndex,
      partnerName: row.name,
      cif: row.cif,
      shares: row.shares,
      holdingClassId,
      holdingClassName: holdingClass?.name || '',
      sharePremium: row.sharePremium,
      shareFrom,
      shareTo,
      isExistingPartner: row.isExistingPartner,
      hasExplicitRange: row.hasExplicitRange,
    };
  });

  return { items, overlapErrors };
};

export const parseExplicitRangeFromRaw = (
  rawRow: Record<string, unknown>,
  getNroPSInitialColumn: (data: Record<string, unknown>) => unknown,
  getNroPSFinalColumn: (data: Record<string, unknown>) => unknown,
  parseShares: (value: unknown) => number
): {
  shareFrom?: number;
  shareTo?: number;
  hasExplicitRange: boolean;
  hasFrom: boolean;
  hasTo: boolean;
} => {
  const fromRaw = getNroPSInitialColumn(rawRow);
  const toRaw = getNroPSFinalColumn(rawRow);
  const hasFrom = hasRangeValue(fromRaw);
  const hasTo = hasRangeValue(toRaw);

  if (!hasFrom && !hasTo) {
    return { hasExplicitRange: false, hasFrom: false, hasTo: false };
  }

  return {
    hasFrom,
    hasTo,
    hasExplicitRange: hasFrom && hasTo,
    shareFrom: hasFrom ? parseShares(fromRaw) : undefined,
    shareTo: hasTo ? parseShares(toRaw) : undefined,
  };
};
